import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import SlideHeader from './SlideHeader';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const IntroductionSlide: React.FC = () => {
  const { sessionDate } = useSelector((state: RootState) => state.presentation);
  
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{ 
      p: 0,
      background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)'
    }}>
      <SlideHeader title="Welcome to Your Financial Review" />

      {/* Content Section */}
      <Box sx={{ 
        px: { xs: 1, sm: 3 }, 
        pb: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #f0f2f5 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at top right, rgba(10, 25, 41, 0.02) 0%, transparent 70%)',
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ 
          p: { xs: 2, sm: 4 }, 
          maxWidth: '800px', 
          mx: 'auto',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Company Name and Welcome Section */}
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, sm: 4 },
              mb: { xs: 3, sm: 5 },
              borderRadius: 2,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid #DEE2E6',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at top right, rgba(184, 134, 11, 0.03) 0%, transparent 70%)',
                borderRadius: 2,
                pointerEvents: 'none'
              }
            }}
          >
            {/* Company Name */}
            <Typography 
              variant="h4" 
              align="center" 
              sx={{ 
                mb: { xs: 2, sm: 4 },
                color: '#B8860B',
                opacity: 0.9,
                letterSpacing: { xs: '0px', sm: '1px' },
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: { xs: '1.4rem', sm: '2.2rem' },
                lineHeight: { xs: 1.2, sm: 1.5 },
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {'Empower\u00A0Financial\nNetwork'}
            </Typography>

            {/* Name and Date Section */}
            <Box sx={{ 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: { xs: 2, sm: 4 }
            }}>
              <Typography variant="h4" sx={{ 
                mb: { xs: 1, sm: 2 }, 
                color: '#0A1929', 
                fontWeight: 600,
                fontSize: { xs: '1.5rem', sm: '2rem' },
                textAlign: 'center'
              }}>
                Welcome Justin Schiano!
              </Typography>
              <Typography variant="h6" sx={{ 
                mb: { xs: 1, sm: 2 }, 
                color: '#495057',
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                textAlign: 'center'
              }}>
                {formatDate(sessionDate)}
              </Typography>
              <Typography variant="h5" sx={{ 
                fontStyle: 'italic',
                color: '#B8860B',
                opacity: 0.9,
                fontSize: { xs: '1.4rem', sm: '1.8rem' },
                fontWeight: 500,
                textAlign: 'center'
              }}>
                Your Path to Financial Freedom Starts Here
              </Typography>
            </Box>
          </Paper>

          {/* What We'll Cover Today Section */}
          <Box sx={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.7)'
          }}>
            <Typography variant="h5" sx={{ 
              mb: { xs: 2, sm: 3 }, 
              color: '#0A1929', 
              fontWeight: 600,
              fontSize: { xs: '1.3rem', sm: '1.5rem' }
            }}>
              What We'll Cover Today
            </Typography>
            <Box sx={{ maxWidth: '600px', width: '100%' }}>
              <Grid container spacing={2}>
                {[
                  { icon: <AssessmentIcon sx={{ color: '#B8860B', opacity: 0.9 }} />, text: "Current Financial Snapshot" },
                  { icon: <CreditScoreIcon sx={{ color: '#B8860B', opacity: 0.9 }} />, text: "Credit Analysis" },
                  { icon: <AccountBalanceIcon sx={{ color: '#B8860B', opacity: 0.9 }} />, text: "Available Options" },
                  { icon: <TrendingUpIcon sx={{ color: '#B8860B', opacity: 0.9 }} />, text: "Next Steps" }
                ].map((item, index) => (
                  <Grid item xs={12} key={index}>
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      p: { xs: 0.5, sm: 1 },
                      '&:hover': {
                        bgcolor: 'rgba(25, 118, 210, 0.04)',
                        borderRadius: 1,
                      }
                    }}>
                      {item.icon}
                      <Typography variant="h6" sx={{ 
                        fontWeight: 400,
                        color: 'text.primary',
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                      }}>
                        {item.text}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default IntroductionSlide;
